import { Link } from 'react-router-dom';
import {
  EntitySummary,
  OrgSummary,
  StoreSummary,
  UserSummary,
  summaryLabel,
} from '@/client/types/payment';
import { cn } from '@/lib/utils';

type RelationshipKind =
  | 'payment'
  | 'invoice'
  | 'order'
  | 'transaction'
  | 'subscription'
  | 'user'
  | 'org'
  | 'entity'
  | 'store'
  | 'wallet'
  | 'card';

const hrefFor = (
  kind: RelationshipKind,
  id: number | string,
  extra?: Record<string, string | number | undefined>,
): string => {
  const qs = new URLSearchParams();
  Object.entries(extra || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v));
  });
  const q = qs.toString();
  const withQ = (base: string, key: string) => `${base}?${key}=${id}${q ? `&${q}` : ''}`;

  switch (kind) {
    case 'payment':
      return withQ('/admin/payments', 'pid');
    case 'invoice':
      return withQ('/admin/payments/invoices', 'invid');
    case 'order':
      return withQ('/admin/payments/orders', 'orderid');
    case 'transaction':
      return withQ('/admin/payments/transactions', 'tid');
    case 'subscription':
      return withQ('/admin/payments/subscriptions', 'plid');
    case 'user':
      return withQ('/admin/users', 'uid');
    case 'org':
      return withQ('/admin/users/organization', 'oid');
    case 'entity':
      return withQ('/admin/listings', 'eid');
    case 'store':
      return withQ('/admin/payments/orders', 'storeid');
    case 'wallet':
      return `/admin/payments/wallets?${q || `uid=${id}`}`;
    case 'card':
      return `/admin/payments/cards?${q || `uid=${id}`}`;
    default:
      return '#';
  }
};

export function RelationshipLink({
  kind,
  id,
  label,
  subtitle,
  className,
  extra,
}: {
  kind: RelationshipKind;
  id?: number | string | null;
  label?: string | null;
  subtitle?: string | null;
  className?: string;
  extra?: Record<string, string | number | undefined>;
}) {
  if (id == null || id === '') {
    return <span className="text-sm text-gray-400">—</span>;
  }

  const text = label || String(id);
  return (
    <Link
      to={hrefFor(kind, id, extra)}
      onClick={(e) => e.stopPropagation()}
      className={cn(
        'inline-flex max-w-full flex-col text-left text-sm font-medium text-blue-700 hover:underline',
        className,
      )}
      title={subtitle || text}
    >
      <span className="truncate">{text}</span>
      {subtitle ? <span className="truncate text-xs font-normal text-gray-500">{subtitle}</span> : null}
    </Link>
  );
}

export function UserRelationshipLink({
  uid,
  summary,
}: {
  uid?: number | null;
  summary?: UserSummary | null;
}) {
  if (uid == null) return <span className="text-sm text-gray-400">—</span>;
  return (
    <RelationshipLink
      kind="user"
      id={uid}
      label={summaryLabel(summary) !== '—' ? summaryLabel(summary) : `User ${uid}`}
      subtitle={summary?.email || (summary?.ffid ? `ffid ${summary.ffid}` : `uid ${uid}`)}
    />
  );
}

export function OrgRelationshipLink({
  oid,
  summary,
}: {
  oid?: number | null;
  summary?: OrgSummary | null;
}) {
  if (oid == null) return <span className="text-sm text-gray-400">—</span>;
  return (
    <RelationshipLink
      kind="org"
      id={oid}
      label={summaryLabel(summary) !== '—' ? summaryLabel(summary) : `Org ${oid}`}
      subtitle={summary?.ffid ? `ffid ${summary.ffid}` : `oid ${oid}`}
    />
  );
}

export function EntityRelationshipLink({
  eid,
  summary,
}: {
  eid?: number | null;
  summary?: EntitySummary | null;
}) {
  if (eid == null) return <span className="text-sm text-gray-400">—</span>;
  return (
    <RelationshipLink
      kind="entity"
      id={eid}
      label={summaryLabel(summary) !== '—' ? summaryLabel(summary) : `Entity ${eid}`}
      subtitle={summary?.ffid || summary?.slug || `eid ${eid}`}
    />
  );
}

export function StoreRelationshipLink({
  storeid,
  summary,
}: {
  storeid?: number | null;
  summary?: StoreSummary | null;
}) {
  if (storeid == null) return <span className="text-sm text-gray-400">—</span>;
  return (
    <RelationshipLink
      kind="store"
      id={storeid}
      label={summaryLabel(summary) !== '—' ? summaryLabel(summary) : `Store ${storeid}`}
      subtitle={summary?.slug || `storeid ${storeid}`}
      extra={{ storeid }}
    />
  );
}
